import React, { useState, useEffect } from 'react';
import { 
    Chart as ChartJS, 
    CategoryScale, 
    LinearScale, 
    BarElement,
    Title, 
    Tooltip, 
    Legend,
    TimeScale
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Bar } from 'react-chartjs-2';
import { req } from '../../utils.js';
import { Card, CardContent, CardHeader } from '@mui/material';
import { differenceInCalendarDays } from 'date-fns';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    TimeScale
);

export const ServiceTechJobsCompleted = ({ dateRanges }) => {
    const [chartData, setChartData] = useState({
        labels: [],
        datasets: []
    });

    useEffect(() => {
        if (!dateRanges || dateRanges.length === 0) return;

        const fetchData = async () => {
            const start = Math.floor(new Date(dateRanges[0]).getTime() / 1000);
            const end = Math.floor(new Date(dateRanges[1]).getTime() / 1000);

            const result = await req('GET', `get_st_completed_jobs?s=${start}&e=${end}`);
            // Determine grouping granularity
            const days = differenceInCalendarDays(new Date(dateRanges[1]), new Date(dateRanges[0]));
            const groupByMonth = days > 31;
            const timeZone = 'Australia/Melbourne';
            const getGroupKey = (timestamp) => {
                const date = new Date(timestamp * 1000);
                // 'en-CA' gives YYYY-MM-DD in the specified locale
                const localDate = date.toLocaleDateString('en-CA', {
                    timeZone,
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                });
                return groupByMonth ? localDate.slice(0, 7) : localDate;
            };

            // Group data by day or month and technician
            const jobsPerGroup = {};
            result.forEach(job => {
                const groupKey = getGroupKey(job.time_completed);
                const techName = job.service_tech;
                if (!jobsPerGroup[groupKey]) {
                    jobsPerGroup[groupKey] = {};
                }
                if (!jobsPerGroup[groupKey][techName]) {
                    jobsPerGroup[groupKey][techName] = 0;
                }
                jobsPerGroup[groupKey][techName]++;
            });

            // Prepare datasets for the chart
            const datasets = [];
            const allTechs = new Set();
            Object.keys(jobsPerGroup).forEach(groupKey => {
                Object.keys(jobsPerGroup[groupKey]).forEach(tech => {
                    allTechs.add(tech);
                });
            });
            const getColors = (tech_id) => {
                const purplePalette = [
                    '#B3A6FF',
                    '#9C6BFF',
                    '#6F32C8'
                ];
                const bluePalette = [
                    '#6EC6FF',
                    '#5AA2D4',
                    '#274A78'
                ];
                const orangePalette = [
                    '#FFC19E',
                    '#FF8A3D',
                    '#D45A2A'
                ];
                const techToPalette = {
                    'Abram McEvoy': purplePalette[0],
                    'Geoff Duggan': purplePalette[1],
                    'Neil Gibson': purplePalette[2],
                    'Matthew Rindfleish': bluePalette[0],
                    'Mark Weber': bluePalette[1],
                    'Lee Pu': bluePalette[2],
                    'Matthew Miliano': orangePalette[0],
                    'Vimal Johnson': orangePalette[1]
                };
                return techToPalette[tech_id] || orangePalette[2];
            };
            Array.from(allTechs).forEach(tech => {
                const data = Object.keys(jobsPerGroup).map(groupKey => ({
                    x: groupKey,
                    y: jobsPerGroup[groupKey][tech] || 0
                }));
                const color = getColors(tech);
                datasets.push({
                    label: tech,
                    data,
                    backgroundColor: color,
                    borderColor: color,
                    tension: 0.1
                });
            });

            setChartData({
                labels: Object.keys(jobsPerGroup).sort(),
                datasets
            });
        };
        fetchData();
    }, [JSON.stringify(dateRanges)]);

    const options = {
        scales: {
            x: {
                type: 'time',
                time: {
                    unit: (() => {
                        if (dateRanges && dateRanges.length === 2) {
                            const days = differenceInCalendarDays(new Date(dateRanges[1]), new Date(dateRanges[0]));
                            return days > 31 ? 'month' : 'day';
                        }
                        return 'day';
                    })()
                },
                title: {
                    display: true,
                    text: 'Date'
                },
                stacked: true
            },
            y: {
                stacked: true,
                title: {
                    display: true,
                    text: 'Number of Jobs'
                }
            }
        },
        plugins: {
            tooltip: {
                mode: 'index'
            }
        }
    };

    return chartData.labels.length ? (
        <Card>
            <CardHeader title="Jobs Completed by Staff" />
            <CardContent sx={{
                maxHeight: '600px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center'
            }}>
                <Bar data={chartData} options={options} />
            </CardContent>
        </Card>
    ) : null;
};
